import { isset, PhpArray } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { array, slot, type Arr } from './access';
import sourceCatalogs from './source-catalogs.json';
import traitCatalogs from './trait-source-catalogs.json';
export function catalogRows(catalog: string): PhpArray {
    const row = (traitCatalogs as Record<string, unknown>)[catalog] ?? (sourceCatalogs as Record<string, unknown>)[catalog];
    if (row === undefined) throw new RangeError('Unknown source catalog');
    return array(row as Arr);
}
/** Static catalog constants resolved from PHP declarations, not case outputs. */
export const CATALOG_IDS: Readonly<Record<string, string>> = Object.freeze({
    "ArmorType::shield": "376E7991-D1E3-D280-5186-F09AE6F90CD8",
    "CampaignHouseRuleType::no_encumbrance": "F1F5285D-E498-4B20-956C-C4CC51015DFC",
    "CasterType::full_caster": "A18765B6-F35A-47E2-F112-DF1582C37B37",
    "CasterType::half_caster": "E8FB3E1F-2B16-4889-831C-99FC9E34853E",
    "RangeType::self": "D82855E1-4BB9-93EA-6374-B9904318BC1D",
    "RollType::contesting_roll": "AD80D31D-898B-66CE-0894-1335D11B1529",
    "RollType::damage_over_time_roll": "77A044B7-7041-CA96-3C63-BB718B15A364",
    "RollType::damage_roll": "82BBB3B6-4646-48E8-A5A2-8DC0947A91D0",
    "RollType::healing_roll": "4FB7452A-F130-40C3-83E6-5E92C329D7B2",
    "RollType::reduce_luck_roll": "DE5B6A74-0024-FFDB-C5C6-B0248D956515",
    "RollType::reduce_mana_roll": "905613F5-A045-BC0A-3686-CD1EC15060C5",
    "RollType::regeneration_roll": "9909BE44-99AE-8780-BB12-BD99BA6E672E",
    "RollType::skill_roll_assistance_roll": "E26FC164-16F5-E38B-66F9-337FAEDE2A67",
    "RollType::skill_roll_retracting_roll": "73ECB998-6C8E-1854-FBBB-EF02AC2AC0EE",
    "RollType::spell_melee_attack_roll": "5740CA49-006E-A7A0-B46B-6DDED6CD546C",
    "RollType::spell_ranged_attack_roll": "DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C",
    "RollType::temporary_max_vitality_roll": "F5BC82EE-DBDF-4594-F850-0C49E4292AC7",
    "RollType::temporary_vitality_regeneration_roll": "4634635E-4E82-DFB3-8411-27A6BFB8CF0F",
    "RollType::temporary_vitality_roll": "5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591",
    "StatusEffect::dead": "9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA",
    "TeamType::enemy": "9399856B-2132-7574-1684-91139A2831DD",
    "TeamType::friend": "7DBEB6D4-D147-703A-022E-05B290BB95C3",
    "TeamType::neutral": "BBF5D6A6-FB9B-E07D-0497-B60EC68D2EBD",
    "VitalityStatus::barely_wounded": "658483AA-34DE-74E5-C974-E3E202C31CC7",
    "VitalityStatus::bloodied": "44B8792E-632E-2651-C666-67DFD181E92A",
    "VitalityStatus::dying": "8AE129AF-52D8-CCE8-706A-85767DB52740",
    "VitalityStatus::fresh": "FF30B76E-785D-2CAF-FC66-C131D0548808",
    "VitalityStatus::invigorated": "82788F70-EBEF-44C4-80E7-6622AD20DD60",
    "VitalityStatus::on_deaths_door": "D5DE14C4-3194-87BC-D767-6D45BDD90552",
    "VitalityStatus::well_bloodied": "298BE1DD-2917-476E-D79C-92CF8C598926",
    "VitalityStatus::wounded": "C97076EF-FED9-19B4-D109-C22F9BC740E8"
});
export function catalogId(catalog: string, variable: string, env: PortEnv<object>): string {
    const key = catalog + '::$idCache';
    const cache = env.globals[key] ?? new PhpArray();
    if (isset(cache, variable)) return slot(cache, variable) as string;
    const id = CATALOG_IDS[catalog + '::' + variable]
        ?? [...catalogRows(catalog).values()].find(row => slot(row, 'variable_name') === variable);
    const resolved = typeof id === 'string' ? id : slot(id, 'item_id');
    if (resolved === null || resolved === undefined) throw new RangeError('Unknown source catalog constant');
    const updated = array(cache as Arr); updated.set(variable, resolved); env.globals[key] = updated;
    return resolved as string;
}
/** HasVariableNameById trait: this reader's cache is PHP static class state. */
export function catalogVariable(catalog: string, id: string, env: PortEnv<object>): string | null {
    const key = catalog + '::$variableNameCache', cache = env.globals[key] ?? new PhpArray();
    if (isset(cache, id)) return slot(cache, id) as string;
    const row = [...catalogRows(catalog).values()].find(row => slot(row, 'item_id') === id);
    const variable = slot(row, 'variable_name') as string | null;
    const next = array(cache as Arr); next.set(id, variable); env.globals[key] = next;
    return variable;
}
export function skillId(variable: string, env: PortEnv<object>): string | null {
    const key = 'Skill::$idCache', cache = env.globals[key] ?? new PhpArray();
    if (isset(cache, variable)) return slot(cache, variable) as string;
    const id = sourceCatalogs.Skill.find(row => row.variable_name === variable)?.item_id ?? null;
    const updated = array(cache as Arr); updated.set(variable, id); env.globals[key] = updated;
    return id;
}
export function catalogVariableName(catalog: 'Skill' | 'AoeType', id: string, env: PortEnv<object>): string | null {
    const key = catalog + '::$variableNameCache', cache = env.globals[key] ?? new PhpArray();
    if (isset(cache, id)) return slot(cache, id) as string;
    const name = sourceCatalogs[catalog].find(row => row.item_id === id)?.variable_name ?? null;
    const updated = array(cache as Arr); updated.set(id, name); env.globals[key] = updated;
    return name;
}
